import type { AxiosInstance } from "axios";
import { isServer } from "util/browser-utils";
import Persistence from "pages/common/lib/grabyo-persistence";
import { api } from "api/base";
import {
    producerGatewayLoginPath,
    producerGatewaySigninRedirectPath
} from "pages/producer/gateway/utils/path.utils";

export const publicPaths = [
    producerGatewayLoginPath,
    producerGatewaySigninRedirectPath,
    `/login`,
    `/player`,
    `/standalonehlsstream.jsp`,
    `/_error`,
    `/healthcheck`,
    `/graphics/templateplayout`,
    `/unsupported`
];
const defaultSuccessHandler = (config) => config;
const defaultErrorHandler = (error) => Promise.reject(error);

/**
 * @summary - Check if the user's JWT authentication token is within 2 minutes of expiring or expired
 */
const shouldRefreshToken = (url: string): boolean => {
    if (!Persistence.PermStore.get(`tokenExpiryDate`)) {
        return false;
    }

    const noAuthEndpoints = [
        `login`,
        `refresh-token`,
        `reset-password`,
        `accept-invite`,
        `logout`
    ];

    if (noAuthEndpoints.find((ep) => url.includes(`/${ep}`))) {
        return false;
    }

    const tokenExpiryDate = Persistence.PermStore.get(`tokenExpiryDate`);
    return tokenExpiryDate && 0 >= tokenExpiryDate - Date.now() - 60 * 2 * 1000;
};

/**
 * @summary - Adds an interceptor to axios instance which refreshes the user auth token if it's expired
 *
 * @param axiosInstance - An axios instance
 */
export const addTokenExpiryInterceptor = (axiosInstance: AxiosInstance) => {
    if (isServer()) {
        return;
    }

    axiosInstance.interceptors.request.use(async (config) => {
        if (shouldRefreshToken(config.url)) {
            await api.refreshToken();
        }

        return config;
    }, defaultErrorHandler);
};

/**
 * @summary - Replace the value of a cookie in a cookie string
 *
 * @param cookies - Cookie string to search in
 * @param cookieName - Name of the cookie to replace
 * @param replacementValue - New cookie value, default "hidden"
 *
 */
export const replaceCookieValue = (
    cookies: string,
    cookieName: string,
    replacementValue = `hidden`
): string => {
    const regex = new RegExp(`${cookieName}=[^;]*`, `gi`);
    return cookies.slice().replace(regex, `${cookieName}=${replacementValue}`);
};

/**
 * @summary - Add an axios interceptor to prevent any sensitive data being logged on the server
 *
 * @param axiosInstance - An axios instance
 */
export const addErrorLogsFilterInterceptor = (axiosInstance: AxiosInstance) => {
    axiosInstance.interceptors.response.use(defaultSuccessHandler, (error) => {
        error.config.headers.cookie = replaceCookieValue(
            error.config.headers.cookie,
            `grabyo-jwt`
        );
        error.request._header = replaceCookieValue(
            error.request._header,
            `grabyo-jwt`
        );
        return Promise.reject(error);
    });
};

/**
 * @summary Create a global jQuery ajax "interceptor" to refresh the user's JWT if it's expired
 */
export const setupJqueryAuthPrefilter = () => {
    //TODO: we shouldn't be using jQuery at all
    $.ajaxPrefilter((options, originalOptions, jqXHR) => {
        if (shouldRefreshToken(options.url)) {
            if (originalOptions.error) {
                originalOptions._error = originalOptions.error;
            }
            options.error = $.noop();

            jqXHR.done = $.noop();
            jqXHR.fail = $.noop();
            jqXHR.abort();

            const dfd = $.Deferred();

            api.refreshToken().then(() => {
                $.ajax(originalOptions).done(dfd.resolve).fail(dfd.reject);
            });
            return dfd.promise(jqXHR);
        }
        return jqXHR;
    });
};

/**
 * @summary - Initializes the service worker
 * @see /studio/src/workers/service-worker/sw.ts
 */
export const registerServiceWorker = () => {
    if (`serviceWorker` in navigator) {
        navigator.serviceWorker.register(`/sw.js`).then(
            (registration) => {
                console.log(
                    `Service Worker registration successful with scope: `,
                    registration.scope
                );
                api.refreshToken();
            },
            (err) => {
                console.log(`Service Worker registration failed: `, err);
            }
        );
    } else {
        // Service worker should be a progressive enhancement, so everything should still work fine without it
        console.warn(`Service Worker not supported by this browser`);
    }
};

/**
 * @summary - Deregisters the service worker
 */
export const unregisterServiceWorker = async () => {
    if (`serviceWorker` in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        registrations.forEach((registration) => registration.unregister());
    }
};
